/**
 * TanStack Query hooks for Gate Rules CRUD operations.
 * FR-061, FR-072 — admin-only mutations, optimistic toggle.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api, ApiError } from '@/lib/api';
import type {
  GateRuleResponse,
  CreateGateRulePayload,
  UpdateGateRulePayload,
  ToggleRuleResponse,
} from '@/types/rules';

export type { GateRuleResponse, RuleSeverity } from '@/types/rules';

// ─── Query keys ──────────────────────────────────────────────────────────────

export const rulesKeys = {
  all: ['rules'] as const,
  list: () => ['rules', 'list'] as const,
};

// ─── Queries ─────────────────────────────────────────────────────────────────

interface GateRulesEnvelope {
  data: GateRuleResponse[];
  meta: { page: number; limit: number; total: number; total_pages: number };
}

export function useGateRules() {
  return useQuery({
    queryKey: rulesKeys.list(),
    queryFn: async () => {
      const res = await api.get<GateRulesEnvelope>('/api/v1/rules');
      return res.data;
    },
  });
}

// ─── Mutations ───────────────────────────────────────────────────────────────

export function useCreateRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateGateRulePayload) =>
      api.post<GateRuleResponse>('/api/v1/rules', payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: rulesKeys.all });
      toast.success('Rule created successfully.');
    },
    onError: (err) => {
      const msg =
        err instanceof ApiError && err.status === 400
          ? 'Validation error. Please check the form fields.'
          : 'Failed to create rule. Please try again.';
      toast.error(msg);
    },
  });
}

export function useUpdateRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateGateRulePayload }) =>
      api.put<GateRuleResponse>(`/api/v1/rules/${id}`, payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: rulesKeys.all });
      toast.success('Rule updated successfully.');
    },
    onError: (err) => {
      const msg =
        err instanceof ApiError && err.status === 404
          ? 'Rule not found. It may have been deleted.'
          : err instanceof ApiError && err.status === 409
          ? 'This rule was modified by another user. Please refresh and try again.'
          : 'Failed to update rule. Please try again.';
      toast.error(msg);
      void qc.invalidateQueries({ queryKey: rulesKeys.all });
    },
  });
}

export function useToggleRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      api.patch<ToggleRuleResponse>(`/api/v1/rules/${id}/toggle`, { enabled }),
    onSuccess: (data) => {
      void qc.invalidateQueries({ queryKey: rulesKeys.all });
      toast.success(data.enabled ? 'Rule enabled.' : 'Rule disabled.');
    },
    onError: () => {
      void qc.invalidateQueries({ queryKey: rulesKeys.all });
      toast.error('Failed to update rule. Please try again.');
    },
  });
}

export function useDeleteRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<unknown>(`/api/v1/rules/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: rulesKeys.all });
      toast.success('Rule deleted.');
    },
    onError: (err) => {
      const msg =
        err instanceof ApiError && err.status === 404
          ? 'Rule not found. It may have already been deleted.'
          : 'Failed to delete rule. Please try again.';
      toast.error(msg);
      void qc.invalidateQueries({ queryKey: rulesKeys.all });
    },
  });
}
